"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, ArrowRight, MapPin, CircleCheck as CheckCircle2, PhoneCall, Sparkles } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "Why should my Colchester business invest in local SEO over general UK SEO?",
  answer: "If your customers are in Colchester, Essex, or surrounding towns like Stanway, Lexden, and Wivenhoe, standard national SEO won't cut it. Local SEO focuses specifically on winning intent-driven searches in your geographical target area—capturing people actively looking for your services right now on Google Maps and localized search results."
}, {
  question: "Will I work directly with you, Yassine, or be passed to a junior account manager?",
  answer: "You work directly with me—100% guaranteed. I am an independent freelance SEO consultant, not a bloated agency with high staff turnover. When you book a call or review a report, you speak directly with the expert who audits, strategist, and executes your local search campaign."
}, {
  question: "How long does it take to see real Colchester search results?",
  answer: "Most Colchester clients start seeing noticeable movement in Google Map Pack rankings and local organic position within 60 to 90 days. Competitive niches like local legal, medical, or home trade services may take 4 to 6 months to reach dominant top-3 positions."
}, {
  question: "How do you optimize my Google Business Profile for Colchester customers?",
  answer: "I perform a granular optimization of your Google Business Profile (GBP), including accurate primary and secondary categories, geo-tagged local photo optimization, structured service lists, keyword-aligned business descriptions, customer review acquisition strategies, and local Q&A management."
}, {
  question: "Can local SEO help my business rank in surrounding Essex areas like Chelmsford and Braintree?",
  answer: "Yes. We build targeted multi-location SEO frameworks and localized content hubs designed to rank your business across neighboring regions such as Braintree, Chelmsford, Clacton-on-Sea, and Harwich without resorting to doorway pages or spammy tactics."
}, {
  question: "How do you handle local Colchester link building and community citations?",
  answer: "I secure high-quality, relevant local links and citations from genuine Essex directories, local business associations, regional media outlets, and community partnerships. I never use low-quality link farms or automated link schemes that risk your domain reputation."
}, {
  question: "What is the cost of hiring an independent freelance local SEO consultant?",
  answer: "Because I don't carry the massive overheads of traditional digital agencies, every pound of your investment goes directly into high-impact strategic SEO work. My retainers and project audits are fully bespoke, tailored precisely to your Colchester business growth goals and market competition."
}, {
  question: "How do you measure success and leads generated from our SEO campaign?",
  answer: "I provide clear, transparent reporting focused on business impact: tracked keyword rankings across Colchester postcodes, Google Business Profile calls and driving directions, organic phone leads, contact form submissions, and verified goal conversions in Google Analytics 4."
}, {
  question: "Can you help optimize my Colchester business for AI search engines like ChatGPT and Google AI Overviews?",
  answer: "Absoutely. AI Search Optimisation (GEO/AIO) is an essential evolution of modern SEO. I optimize your web entity, structured schema data, and online mentions so your Colchester brand is authoritative enough to be featured in AI-generated answers and recommendations."
}, {
  question: "What happens if my website already has poor historical SEO or Google penalties?",
  answer: "During our initial consultation, I conduct a thorough diagnostic check of your site's historical search footprint, backlink health, and technical profile to clean up toxic issues, resolve indexing blockers, and build a clean path forward."
}, {
  question: "How does local SEO differ for Colchester B2B companies versus local service providers?",
  answer: "B2B local SEO focuses heavily on high-intent regional search queries, authoritative thought-leadership content, technical website performance, and localized B2B lead generation, whereas local consumer services prioritize Google Maps rankings, local reviews, and immediate call actions."
}, {
  question: "How do I get started with a free Colchester SEO consultation?",
  answer: "Simply book a free consultation through my website. I will personally review your existing Colchester search visibility, benchmark your key local competitors, and discuss actionable strategies during a direct 1-on-1 discovery session."
}];
export default function Colchester() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="colchesterlocalseofaq" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none -translate-x-1/3 translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary/80 border border-secondary">
            <MapPin className="size-3.5 text-primary" />
            <span>Colchester Local Search Guide</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Frequently Asked Questions About{" "}
            <span className="text-primary underline decoration-secondary decoration-wavy underline-offset-8">
              Colchester Local SEO
            </span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Straight answers about how I help businesses in Colchester and Essex generate sustainable, high-converting organic search traffic—without agency fluff.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="flex items-start gap-3 p-5 rounded-xl bg-card border border-border/80 shadow-xs">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground font-default mb-1">
                Direct Expert Access
              </h3>
              <p className="text-xs text-muted-foreground font-default leading-normal">
                You work directly with Yassine Essaadi on every aspect of your local SEO strategy.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-5 rounded-xl bg-card border border-border/80 shadow-xs">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
              <MapPin className="size-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground font-default mb-1">
                Colchester & Essex Focus
              </h3>
              <p className="text-xs text-muted-foreground font-default leading-normal">
                Tailored optimization explicitly targeting high-value regional search customers.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-5 rounded-xl bg-card border border-border/80 shadow-xs">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
              <Sparkles className="size-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground font-default mb-1">
                Modern & AI-Ready
              </h3>
              <p className="text-xs text-muted-foreground font-default leading-normal">
                Future-proofed search optimization across Google Maps, organic, and AI search engines.
              </p>
            </div>
          </div>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="max-w-4xl mx-auto bg-card rounded-2xl border border-border p-6 sm:p-8 lg:p-10 shadow-sm mb-16">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <motion.div key={i} variants={itemVariants} data-index={i}>
                <AccordionItem value={`item-${i}`} className="border border-border/60 rounded-xl px-4 sm:px-6 bg-background transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-xs">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-primary hover:text-primary/90 text-left py-5 font-default gap-4 [&[data-state=open]]:text-primary">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="size-5 text-accent shrink-0 hidden sm:inline-block" />
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground font-default leading-relaxed pb-6 pt-1 border-t border-border/40">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>)}
          </Accordion>
        </motion.div>

        {}
        <div className="max-w-4xl mx-auto rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 shadow-lg relative overflow-hidden text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground mb-1">
              <MessageSquare className="size-3.5" />
              <span>Have a specific Colchester SEO question?</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-default text-primary-foreground">
              Let's discuss your local search growth
            </h3>
            <p className="text-sm text-primary-foreground/90 font-default">
              Book a direct 1-on-1 SEO consultation with me, Yassine Essaadi. No pushy sales reps—just clear, actionable insight.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 shadow-sm font-semibold">
              <Link to="/ContactPage">
                <span>Book a Free Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent">
              <Link to="/CaseStudiesPage">
                <span>See Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}